import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'
import { employeesQueryOptions } from '../api/queries'
import { type Employee, employeeLabel } from '../model/schema'

export interface EmployeeOption {
  subject: string
  name: string
}

interface EmployeePickerProps {
  id?: string
  value: EmployeeOption[]
  onChange: (next: EmployeeOption[]) => void
  disabled?: boolean
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

const SEARCH_DEBOUNCE_MS = 300
const MAX_SUGGESTIONS = 8

// Кого позвать: выбранные — чипами в поле, найденные — чипами под ним (как в
// тег-поле). Значение наружу — subject'ы: имя приезжает из справочника хаба,
// клиент его не сочиняет.
export function EmployeePicker({ id, value, onChange, disabled, ...aria }: EmployeePickerProps) {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  const employees = useQuery(employeesQueryOptions(debounced))
  const chosen = new Set(value.map((v) => v.subject))
  const suggestions = (employees.data ?? [])
    .filter((e: Employee) => !chosen.has(e.subject))
    .slice(0, MAX_SUGGESTIONS)

  function add(e: Employee) {
    onChange([...value, { subject: e.subject, name: employeeLabel(e) }])
    setQuery('')
  }

  function remove(subject: string) {
    onChange(value.filter((v) => v.subject !== subject))
  }

  return (
    <div>
      {/* label-обёртка: клик по полю фокусирует инпут нативно */}
      <label className="flex w-full cursor-text flex-wrap items-center gap-2 rounded-control border border-border-default bg-bg-sunken px-4 py-3 transition-colors focus-within:border-border-accent">
        {value.map((v) => (
          <Chip key={v.subject} tone="info" className="normal-case">
            {v.name}
            <button
              type="button"
              aria-label={`Убрать ${v.name}`}
              className="text-text-tertiary transition-colors hover:text-text-primary"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                remove(v.subject)
              }}
            >
              <Icon name="close" size={12} />
            </button>
          </Chip>
        ))}
        <input
          id={id}
          {...aria}
          className="min-w-28 flex-1 bg-transparent text-text-primary placeholder:text-text-tertiary focus:outline-none"
          value={query}
          disabled={disabled}
          placeholder={value.length === 0 ? 'Имя или логин сотрудника' : undefined}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {suggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestions.map((e) => (
            <button
              key={e.subject}
              type="button"
              className="rounded-control focus:outline-none focus-visible:ring-1 focus-visible:ring-border-accent"
              disabled={disabled}
              onClick={() => add(e)}
            >
              <Chip
                variant="soft"
                className="gap-1 text-text-tertiary transition-colors hover:bg-bg-surface hover:text-text-primary"
              >
                <Icon name="plus" size={12} />
                {employeeLabel(e)}
              </Chip>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
